<div class="ocr-pane">
  <div class="ocr-pane-head">
    <div>
      <p class="step">Step 3 — Review</p>
      <h2>Case review</h2>
    </div>
    <a routerLink="/document-ocr/documents" class="btn">Back to documents</a>
  </div>

  @if (message()) {
    <p class="ocr-msg">{{ message() }}</p>
  }
  @if (error()) {
    <p class="ocr-msg error">{{ error() }}</p>
  }

  @if (loading()) {
    <div class="empty-box">Loading…</div>
  } @else if (extraction()) {
    @let ext = extraction()!;
    @if (isResolved()) {
      <div class="resolved-banner">
        This case has been confirmed and linked to
        <a [routerLink]="['/complaints', ext.grievanceRef]">grievance #{{ ext.grievanceRef }}</a>.
      </div>
    } @else if (isRejected()) {
      <div class="resolved-banner rejected">This extraction was rejected — no case was created.</div>
    }

    <div class="review-split">
      <div class="doc-panel">
        <div class="doc-panel-head">
          <h3>Source document</h3>
          <button type="button" class="icon-btn" title="Download original file" (click)="downloadSource()">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M12 3v12" /><path d="m7 10 5 5 5-5" /><path d="M5 21h14" />
            </svg>
          </button>
        </div>
        <div class="doc-viewer">
          @if (previewKind() === 'image' && previewUrl()) {
            <a class="preview-frame" [href]="previewUrl()" target="_blank" rel="noopener">
              <img [src]="previewUrl()" alt="Uploaded scan preview" />
            </a>
          } @else if (previewKind() === 'pdf' && previewUrl()) {
            <div class="preview-frame preview-frame-pdf">
              <iframe [src]="safeEmbedUrl()" title="Uploaded scan preview"></iframe>
              <a class="preview-expand" [href]="previewUrl()" target="_blank" rel="noopener">Open full size ↗</a>
            </div>
          } @else if (previewKind() === null) {
            <div class="preview-frame preview-loading">Loading preview…</div>
          } @else {
            <div class="preview-frame preview-loading">Preview unavailable — use download instead.</div>
          }
        </div>
      </div>

      <div class="meta-panel">
        <div class="meta-topbar">
          @if (batch(); as b) {
            <span class="meta-stat batch-id">
              BATCH #{{ b.id }}
              <button type="button" class="icon-btn tiny" title="Copy batch ID" (click)="copyBatchId()">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <rect x="9" y="9" width="12" height="12" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" />
                </svg>
              </button>
            </span>
            @if (b.pageCount != null) {
              <span class="meta-stat">PAGES <b>{{ b.pageCount }}</b></span>
            }
            <span class="meta-stat">COMPLETED <b>{{ completedCount() }}</b></span>
            <span class="meta-stat">REMAINING <b>{{ remainingCount() }}</b></span>
          }
        </div>

        <div class="meta-tabs" role="tablist">
          <button type="button" role="tab" [class.active]="activeTab() === 'details'" (click)="activeTab.set('details')">
            Important details
          </button>
          <button type="button" role="tab" [class.active]="activeTab() === 'matches'" (click)="activeTab.set('matches')">
            Possible matches
            @if (ext.matchCandidates.length) {
              <span class="tab-badge">{{ ext.matchCandidates.length }}</span>
            }
          </button>
          <button type="button" role="tab" [class.active]="activeTab() === 'text'" (click)="activeTab.set('text')" [disabled]="!ext.rawOcrText">
            Extracted text
          </button>
        </div>

        <div class="meta-tab-body">
          @switch (activeTab()) {
            @case ('details') {
              <h4 class="field-section-title">Extracted fields</h4>
              <div class="field-grid">
                @for (f of fields; track f.key) {
                  @let conf = confidenceFor(f.key);
                  <label
                    [class.field-confident]="conf != null && conf >= 0.95"
                    [class.field-flagged]="conf != null && conf < 0.85"
                  >
                    <span class="field-label-row">
                      <span class="field-label-text">{{ f.label }}</span>
                      @if (conf != null) {
                        <span
                          class="confidence-chip"
                          [class.high]="conf >= 0.95"
                          [class.mid]="conf >= 0.85 && conf < 0.95"
                          [class.low]="conf < 0.85"
                          [attr.title]="(conf * 100 | number: '1.0-0') + '% confidence'"
                        >
                          <i class="confidence-dot" aria-hidden="true"></i>{{ conf * 100 | number: '1.0-0' }}%
                        </span>
                      }
                    </span>
                    @if (f.key === 'department') {
                      <select
                        [ngModel]="values[f.key] || ''"
                        (ngModelChange)="values[f.key] = $event"
                        [disabled]="isResolved() || isRejected()"
                      >
                        <option value="">— Not detected, pick one —</option>
                        @for (d of departments(); track d) {
                          <option [value]="d">{{ d }}</option>
                        }
                      </select>
                    } @else {
                      <input
                        [lang]="f.key === 'subjectHindi' ? 'hi' : 'en'"
                        [ngModel]="values[f.key] || ''"
                        (ngModelChange)="values[f.key] = $event"
                        [disabled]="isResolved() || isRejected()"
                      />
                    }
                  </label>
                }
              </div>

              @if (summaryFields.length) {
                <h4 class="field-section-title">Summary</h4>
                <div class="summary-card">
                  @for (f of summaryFields; track f.key) {
                    @let conf = confidenceFor(f.key);
                    <label
                      [class.field-confident]="conf != null && conf >= 0.95"
                      [class.field-flagged]="conf != null && conf < 0.85"
                    >
                      <span class="field-label-row">
                        <span class="field-label-text">{{ f.label }}</span>
                        @if (conf != null) {
                          <span
                            class="confidence-chip"
                            [class.high]="conf >= 0.95"
                            [class.mid]="conf >= 0.85 && conf < 0.95"
                            [class.low]="conf < 0.85"
                            [attr.title]="(conf * 100 | number: '1.0-0') + '% confidence'"
                          >
                            <i class="confidence-dot" aria-hidden="true"></i>{{ conf * 100 | number: '1.0-0' }}%
                          </span>
                        }
                      </span>
                      <textarea
                        appAutoGrow
                        rows="3"
                        [lang]="f.key === 'summaryHindi' ? 'hi' : 'en'"
                        [ngModel]="values[f.key] || ''"
                        (ngModelChange)="values[f.key] = $event"
                        [disabled]="isResolved() || isRejected()"
                      ></textarea>
                    </label>
                  }
                </div>
              }

              @if (!isResolved() && !isRejected()) {
                <div class="save-row">
                  <button type="button" class="btn" (click)="save()" [disabled]="saving()">Save corrections</button>
                </div>
              }
            }
            @case ('matches') {
              @if (!ext.matchCandidates.length) {
                <p class="hint">No similar existing cases were found — safe to create as new.</p>
              } @else {
                <ul class="candidates-list">
                  @for (c of ext.matchCandidates; track c.grievanceId) {
                    <li>
                      <label class="candidate" [class]="matchTier(c.score)">
                        <input
                          type="radio"
                          name="candidate"
                          [value]="c.grievanceId"
                          [ngModel]="selectedCandidate()"
                          (ngModelChange)="selectedCandidate.set($event)"
                          [disabled]="isResolved() || isRejected() || !isLinkable(c.score)"
                        />
                        <span class="score-ring" [style.background]="'conic-gradient(currentColor 0 ' + (c.score * 100) + '%, var(--line) 0)'">
                          <span class="score-ring-inner">{{ (c.score * 100) | number: '1.0-0' }}</span>
                        </span>
                        <span class="candidate-main">
                          <span class="candidate-title">Case #{{ c.grievanceId }}</span>
                          <strong class="tier-label">{{ matchTierLabel[matchTier(c.score)] }}</strong>
                        </span>
                        <span class="candidate-check" aria-hidden="true">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M5 13l4 4L19 7" /></svg>
                        </span>
                      </label>
                    </li>
                  }
                </ul>
                @if (!isLinkable(ext.matchCandidates[0].score)) {
                  <div class="recommend-callout">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 2 9.5 8.5 3 11l6.5 2.5L12 20l2.5-6.5L21 11l-6.5-2.5Z" /></svg>
                    <span>None of these look like a real match — <strong>Create as new case</strong> is probably right.</span>
                  </div>
                }
              }
            }
            @case ('text') {
              @if (ext.rawOcrText) {
                <pre class="raw-text-block">{{ ext.rawOcrText }}</pre>
              } @else {
                <p class="hint">No OCR transcription was captured for this extraction.</p>
              }
            }
          }
        </div>

        @if (!isResolved() && !isRejected()) {
          <div class="meta-actions">
            <button
              type="button"
              class="btn primary"
              (click)="linkToSelected()"
              [disabled]="saving() || selectedCandidate() == null"
            >
              Link to selected case
            </button>
            <button type="button" class="btn" (click)="createNew()" [disabled]="saving()">
              Create as new case
            </button>
            <button type="button" class="btn danger" (click)="reject()" [disabled]="saving()">
              Reject — not a real case
            </button>
          </div>
        }
      </div>
    </div>
  }
</div>
